"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const HealthcareClient = () => {
    const { t, translationsLoaded } = useComponentTranslations('Healthcare');
    const ht = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems = [
        { title: ht('healthcareExpertise1'), icon: 'carbon:analytics' },
        { title: ht('healthcareExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: ht('healthcareExpertise3'), icon: 'carbon:rule' },
        { title: ht('healthcareExpertise4'), icon: 'carbon:security' },
        { title: ht('healthcareExpertise5'), icon: 'carbon:cloud-app' },
        { title: ht('healthcareExpertise6'), icon: 'carbon:blockchain' },
        { title: ht('healthcareExpertise7'), icon: 'carbon:data-refinery' },
        { title: ht('healthcareExpertise8'), icon: 'carbon:chart-line-data' },
        { title: ht('healthcareExpertise9'), icon: 'carbon:user-profile' },
    ];

    const useCasesForAccordion = [
        { title: ht('healthcareUseCase1'), description: ht('healthcareUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: ht('healthcareUseCase2'), description: ht('healthcareUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: ht('healthcareUseCase3'), description: ht('healthcareUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: ht('healthcareUseCase4'), description: ht('healthcareUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    const servicesForAlternating = [
        { title: ht('healthcareService1Title'), description: ht('healthcareService1Desc'), image: '/images/finance.jpg' },
        { title: ht('healthcareService2Title'), description: ht('healthcareService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: ht('healthcareService3Title'), description: ht('healthcareService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: ht('healthcareService4Title'), description: ht('healthcareService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: ht('healthcareService5Title'), description: ht('healthcareService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems = [
        { question: ht('healthcareFaq1q'), answer: ht('healthcareFaq1a') },
        { question: ht('healthcareFaq2q'), answer: ht('healthcareFaq2a') },
        { question: ht('healthcareFaq3q'), answer: ht('healthcareFaq3a') },
        { question: ht('healthcareFaq4q'), answer: ht('healthcareFaq4a') },
        { question: ht('healthcareFaq5q'), answer: ht('healthcareFaq5a') },
    ];

    return (
        <div className="relative min-h-screen bg-transparent text-white">
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {ht('healthcareHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    {ht('healthcareHeroSubtitle')}
                </motion.p>
            </header>

            <section className="w-full py-24 bg-white/5 border-y border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{ht('healthcarePainTitle')}</h2>
                    <p className="text-lg text-white/70 text-center max-w-4xl mx-auto">
                        {ht('healthcarePainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{ht('healthcareExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 border border-white/10 rounded-lg bg-white/5 flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={ht('healthcareServicesTitle')} useCases={servicesForAlternating} />
            </div>

            <ServicesAccordion title={ht('healthcareUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={ht('healthcareTechTitle')} t={t} />
                <IndustrialsForm label={ht('healthcareCtaTitle')} />

                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">{ht('healthcareFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 border border-white/10 rounded-lg bg-white/5">
                                <summary className="font-semibold cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default HealthcareClient;
